import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Input, Space, Tag, Avatar, Button, Badge, Select, Tooltip, Card, Row, Col, Skeleton, Switch } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  FilterOutlined,
  TeamOutlined,
  WifiOutlined,
  StopOutlined,
  RiseOutlined,
  EnvironmentOutlined,
  StarFilled,
} from '@ant-design/icons';
import { CallNotesModal } from '../../components/CallNotesModal';
import { CallButton, LanguageFlag, RolePair } from '../../components/UserBadges';
import { CallColorFilters, type CallColor } from '../../components/CallColorFilters';
import { LanguageFilter, type LanguageFilterValue } from '../../components/LanguageFilter';
import { useEffect, useRef, useState } from 'react';
import { UserFilters, UserFilterValues } from '../../components/filters/UserFilters';
import { formatRegisteredAt, resolveCallIconColor } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';
import { buildUserListFilters, countDrawerFilters } from '../../lib/userListFilters';

interface Passenger {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: 'CLIENT' | 'DRIVER' | 'ADMIN';
  initialRole: 'CLIENT' | 'DRIVER' | 'ADMIN' | null;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  photoUrl: string | null;
  totalTrips: number;
  createdAt: string;
  preferredLang: 'UZ' | 'RU' | 'EN' | null;
  callNotesCount?: number;
  callIconColor?: 'red' | 'green' | 'blue' | 'yellow' | 'gray' | 'black' | 'brown' | null;
}

export const PassengerList = () => {
  const { show } = useNavigation();
  const [filterOpen, setFilterOpen] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [drawerFilters, setDrawerFilters] = useState<UserFilterValues>({});
  const [callColorFilter, setCallColorFilter] = useState<CallColor | null>(null);
  const [languageFilter, setLanguageFilter] = useState<LanguageFilterValue>(null);
  const [statusSelect, setStatusSelect] = useState<string | undefined>(undefined);
  // When false (default), "Rasvo qilinganlar" (brown call-colour tag)
  // passengers are hidden — same convention as DriverList.
  const [showRasvo, setShowRasvo] = useState(false);
  const searchTimerRef = useRef<number | null>(null);

  const activeFilterCount = countDrawerFilters(drawerFilters);

  const { tableProps, setFilters, tableQueryResult } = useTable<Passenger>({
    resource: 'admin/users',
    syncWithLocation: true,
    pagination: {
      pageSize: 20,
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    filters: {
      permanent: [{ field: 'role', operator: 'eq', value: 'CLIENT' }],
      // Hide "Rasvo qilinganlar" (brown) passengers by default; the toggle
      // below the title flips this off.
      initial: [{ field: 'excludeCallColor', operator: 'eq', value: 'brown' }],
    },
  });

  // Use `'k' in next` to detect changes — treating the Select's clear-X
  // output (`undefined`) as a deliberate clear instead of "no change".
  const commit = (next: {
    search?: string;
    drawer?: UserFilterValues;
    callColor?: CallColor | null;
    language?: LanguageFilterValue;
    status?: string | undefined;
    showRasvo?: boolean;
  }) => {
    const search = 'search' in next ? next.search! : searchText;
    const drawer = 'drawer' in next ? next.drawer! : drawerFilters;
    const callColor = 'callColor' in next ? next.callColor! : callColorFilter;
    const language = 'language' in next ? next.language! : languageFilter;
    const status = 'status' in next ? next.status : statusSelect;
    const rasvo = 'showRasvo' in next ? next.showRasvo! : showRasvo;
    if ('search' in next) setSearchText(search);
    if ('drawer' in next) setDrawerFilters(drawer);
    if ('callColor' in next) setCallColorFilter(callColor);
    if ('language' in next) setLanguageFilter(language);
    if ('status' in next) setStatusSelect(status);
    if ('showRasvo' in next) setShowRasvo(rasvo);
    // Exclude brown unless the toggle is on, or the user explicitly filters
    // to the brown call-colour (in which case they want to see them).
    const excludeCallColor = rasvo || callColor === 'brown' ? null : 'brown';
    setFilters(
      buildUserListFilters({ search, drawer, callColor, excludeCallColor, language, status }),
      'replace',
    );
  };

  // Stat cards above the table.
  const [stats, setStats] = useState<{
    total: number;
    online: number;
    blocked: number;
    newThisMonth: number;
    totalTrips: number;
    averageRating: number | null;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/admin/passengers-stats', {
          params: showRasvo ? {} : { excludeCallColor: 'brown' },
        });
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRasvo]);

  const [callModalUser, setCallModalUser] = useState<Passenger | null>(null);

  const handleSearchChange = (val: string) => {
    setSearchText(val);
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(
      () => commit({ search: val }),
      val === '' ? 0 : 350,
    );
  };

  const handleDrawerApply = (drawer: UserFilterValues) => {
    const { search, status, ...rest } = drawer;
    const update: any = { drawer: rest };
    if (search !== undefined) update.search = search;
    if (status !== undefined) update.status = status;
    commit(update);
  };

  const handleResetFilters = () => {
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    commit({
      search: '',
      drawer: {},
      callColor: null,
      language: null,
      status: undefined,
      showRasvo: false,
    });
  };

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
  }, []);

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'default',
    BLOCKED: 'red',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Faol',
    OFFLINE: 'Nofaol',
    BLOCKED: 'Bloklangan',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => <span style={{ fontWeight: 500 }}>#{id}</span>,
    },
    {
      title: "Yo'lovchi",
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: string, record: Passenger) => (
        <Space>
          <Avatar src={record.photoUrl} icon={<UserOutlined />} />
          <div>
            <div>{record.fullName || "Ismi yo'q"}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('table.role'),
      key: 'role',
      render: (_: unknown, record: Passenger) => (
        <RolePair currentRole={record.currentRole} initialRole={record.initialRole} />
      ),
    },
    {
      title: t('users.language'),
      key: 'preferredLang',
      width: 60,
      render: (_: unknown, record: Passenger) => <LanguageFlag lang={record.preferredLang} />,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Sayohatlar',
      dataIndex: 'totalTrips',
      key: 'totalTrips',
    },
    {
      title: "Ro'yxatdan o'tgan",
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      defaultSortOrder: 'descend' as const,
      render: (date: string) => formatRegisteredAt(date),
    },
    {
      title: '',
      key: 'call',
      width: 70,
      render: (_: unknown, record: Passenger) => {
        const color = resolveCallIconColor(record.callIconColor, record.callNotesCount);
        const called = (record.callNotesCount ?? 0) > 0;
        return (
          <Tooltip
            title={
              record.callIconColor
                ? `Цветная метка: ${record.callIconColor}`
                : called
                  ? `Был(а) звонок: ${record.callNotesCount}`
                  : 'Звонков ещё не было — добавить заметку'
            }
          >
            <CallButton
              color={color}
              onClick={(e) => {
                e.stopPropagation();
                setCallModalUser(record);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: Passenger) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            show('admin/passengers', record.id);
          }}
        />
      ),
    },
  ];

  const statCards = stats
    ? [
        {
          key: 'total',
          title: t('passengersStats.total'),
          value: stats.total,
          icon: <TeamOutlined />,
          color: '#1F2937',
          bg: '#F3F4F6',
        },
        {
          key: 'online',
          title: t('passengersStats.online'),
          value: stats.online,
          icon: <WifiOutlined />,
          color: '#0EA5E9',
          bg: '#F0F9FF',
        },
        {
          key: 'blocked',
          title: t('passengersStats.blocked'),
          value: stats.blocked,
          icon: <StopOutlined />,
          color: '#EF4444',
          bg: '#FEF2F2',
        },
        {
          key: 'newThisMonth',
          title: t('passengersStats.newThisMonth'),
          value: stats.newThisMonth,
          icon: <RiseOutlined />,
          color: '#8B5CF6',
          bg: '#F5F3FF',
        },
        {
          key: 'totalTrips',
          title: t('passengersStats.totalTrips'),
          value: stats.totalTrips,
          icon: <EnvironmentOutlined />,
          color: '#10B981',
          bg: '#ECFDF5',
        },
        {
          key: 'rating',
          title: t('passengersStats.averageRating'),
          value:
            stats.averageRating != null ? Number(stats.averageRating).toFixed(2) : '—',
          icon: <StarFilled />,
          color: '#F59E0B',
          bg: '#FFFBEB',
        },
      ]
    : [];

  return (
    <>
      {/* Stat cards above the table — match DriverList visual language. */}
      {statsLoading ? (
        <Card style={{ marginBottom: 16 }}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      ) : stats ? (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {statCards.map((c) => (
            <Col key={c.key} xs={12} sm={8} md={6} lg={4}>
              <Card
                size="small"
                bordered={false}
                style={{ background: c.bg, borderRadius: 10, height: '100%' }}
                bodyStyle={{ padding: 12 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: c.color,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {c.icon}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#6B7280',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.title}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: c.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {c.value}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : null}

      <List
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 6 }}>
            <span>Yo'lovchilar</span>
            <CallColorFilters
              value={callColorFilter}
              onChange={(color) => commit({ callColor: color })}
            />
            <LanguageFilter
              value={languageFilter}
              onChange={(v) => commit({ language: v })}
            />
            <Tooltip title="Rasvo qilingan yo'lovchilarni ko'rsatish / yashirish">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                <Switch
                  size="small"
                  checked={showRasvo}
                  onChange={(checked) => commit({ showRasvo: checked })}
                />
                <span style={{ fontSize: 13, fontWeight: 400, color: '#7F8C8D' }}>
                  Rasvo qilinganlar
                </span>
              </span>
            </Tooltip>
          </span>
        }
        headerButtons={
          <Space>
            <Input
              placeholder="Qidirish..."
              prefix={<SearchOutlined />}
              allowClear
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Holat"
              allowClear
              style={{ width: 130 }}
              value={statusSelect}
              onChange={(value) => commit({ status: value })}
              options={[
                { value: 'ONLINE', label: 'Faol' },
                { value: 'OFFLINE', label: 'Nofaol' },
                { value: 'BLOCKED', label: 'Bloklangan' },
              ]}
            />
            <Badge count={activeFilterCount} size="small">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen(true)}
              >
                Filtr
              </Button>
            </Badge>
          </Space>
        }
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => show('admin/passengers', record.id),
            style: { cursor: 'pointer' },
          })}
        />
      </List>

      <UserFilters
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleDrawerApply}
        onReset={handleResetFilters}
        filterType="passengers"
        initialValues={drawerFilters}
      />

      <CallNotesModal
        open={!!callModalUser}
        userId={callModalUser?.id ?? null}
        userName={callModalUser?.fullName ?? undefined}
        userPhone={callModalUser?.phone}
        currentColor={callModalUser?.callIconColor ?? null}
        onClose={() => setCallModalUser(null)}
        onChanged={() => tableQueryResult?.refetch?.()}
      />
    </>
  );
};
